// utils/download.js
import { API_URL } from "../creds";

/**
 * Downloads the translated file for a given task.
 * 
 * @param {string} taskid - The task ID
 * @param {string} [originalTargetFilename] - Original filename (used to suggest name & sent to backend)
 * @param {string} [token] - Authorization token (defaults to localStorage)
 * @returns {Promise<void>}
 */
export const downloadFile = async (
  taskid,
  originalTargetFilename = null,
  token = localStorage.getItem("access_token")
) => {
  if (!taskid) {
    alert("No task ID provided");
    return;
  }

  if (!token) {
    alert("No authentication token found. Please log in again.");
    return;
  }

  try {
    // Build query params safely
    const params = new URLSearchParams({ taskid });
    if (originalTargetFilename) {
      params.append("tgt_file", originalTargetFilename);
    }

    const response = await fetch(`${API_URL}/mde-get-translated-file?${params.toString()}`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "text/markdown, text/plain, application/octet-stream, */*",
      },
    });

    if (!response.ok) {
      let errorMessage = `Download failed (${response.status})`;
      try {
        const errorData = await response.json();
        errorMessage = errorData.detail || errorData.message || errorMessage;
      } catch {
        // fallback if not JSON
      }
      throw new Error(errorMessage);
    }

    // ──────────────────────────────────────────────
    // Get filename – prefer server suggestion → fallback → generated
    // ──────────────────────────────────────────────
    let filename = `${taskid}.md`; // default fallback

    const disposition = response.headers.get("Content-Disposition");
    if (disposition) {
      const filenameMatch = disposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
      if (filenameMatch?.[1]) {
        filename = decodeURIComponent(filenameMatch[1].trim().replace(/^["']|["']$/g, ""));
      }
    } else if (originalTargetFilename) {
      // Use original stem + .md if no server header
      const stem = originalTargetFilename.replace(/\.[^/.]+$/, "");
      filename = `${stem}.md`;
    }

    // ──────────────────────────────────────────────
    // Handle binary/text safely using Blob
    // ──────────────────────────────────────────────
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);

    // Trigger download
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();

    // Cleanup (small delay helps some browsers)
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);

  } catch (err) {
    console.error("Download failed:", err);
    alert(`Could not download file:\n${err.message}`);
    // If you use toast:
    // toast.error(`Download failed: ${err.message}`, { duration: 6000 });
  }
};